// WARNING: Always use the default export (StableInterviewPage) from this file.
// Never import InterviewPage directly. Use only the default export in routes and parent components.
// This prevents remount loops and ensures robust, stable interview sessions.
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import { InterviewProvider, useInterview } from '../contexts/InterviewContext';
import ParticleBackground from '../../../components/ParticleBackground';
import InterviewNavbar from '../components/layout/InterviewNavbar';
import ControlBar from '../components/layout/ControlBar';
import ErrorBoundary from '../../../components/common/ErrorBoundary';
import { useToast } from '../../../components/common/Toast';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import withPageTransition from '../../../utils/withTransition';
import RestartConfirmationModal from '../modals/RestartConfirmationModal';
import EndInterviewConfirmationModal from '../modals/EndInterviewConfirmationModal';
import ShortInterviewWarningModal from '../modals/ShortInterviewWarningModal';
import ConnectionErrorModal from '../modals/ConnectionErrorModal';
import TimerCompletionModal from '../modals/TimerCompletionModal';
import SubscriptionMinutesModal from '../modals/SubscriptionMinutesModal';
import InterviewMainContent from '../components/layout/InterviewMainContent';
import MessageInput from '../components/layout/MessageInput';
import MessageBottomSheet from '../components/modals/MessageBottomSheet';
import AudioSocketHandler from '../components/socket/AudioSocketHandler';
import VideoSocketHandler from '../components/socket/VideoSocketHandler';
import ScreenshotHandler from '../components/socket/ScreenshotHandler';
import useScreenShareTracker from '../../../hooks/useScreenShareTracker';
import { LiveAPIProvider, useLiveAPIContext } from '../contexts/LiveAPIContext';
import { MultimodalLiveClient } from '../../../lib/multimodal-live-client';
import { InterviewFeedback } from '../../../types/interviews';
import { Button } from '../../../components/common/Button';
import ReconnectionIndicator from '../components/ReconnectionIndicator';
import GeneratingFeedbackModal from '../modals/GeneratingFeedbackModal';
import WelcomeAnimation from '../components/WelcomeAnimation';
// @ts-ignore - Avoid type conflicts with multiple Window interface extensions
declare global {
  interface Window {
    // Add properties that need to be accessible on the Window object
    gc?: () => void;
    _currentInterviewMountCount?: number;
    _stableInterviewKey?: string;
    mocktrailWsConnections?: WebSocket[];
    mocktrailMountCycles?: { component: string; mountTime: number }[];
    mocktrailAudioContexts?: Array<AudioContext>;
    altairTimers?: Array<number | NodeJS.Timeout>;
    interviewPageFullyStable?: boolean; // Flag to indicate the interview page is fully stable
    isProcessingInterviewCompletion?: boolean; // Flag to block navigation during interview completion
  }
}

interface InterviewPageProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  videoStream: MediaStream | null;
  onInterviewComplete: (interviewId: string, seriesId: string | undefined, feedbackApplicable: boolean, feedbackData?: InterviewFeedback | null) => Promise<void>;
  onVideoStreamChange: (stream: MediaStream | null) => void;
  userData?: {
    name?: string;
    profileImage?: string;
    interviewGoals?: string[] | null;
    minutesLeft?: number;
  };
  implementorUniqueId?: string; // For public implementor interviews
  isPublicInterview?: boolean; // Flag to indicate this is a public interview (no auth required)
}

/* CSS variables for the interview page */
const cssVariables = `
  :root {
    --navbar-height: 56px;
    --controlbar-height: 72px;
    --content-padding: 8px;
    --content-spacing: 8px;
  }
  
  @media (max-width: 768px) {
    :root {
      --navbar-height: 56px;
      --controlbar-height: 68px;
      --content-padding: 6px;
      --content-spacing: 6px;
    }
  }
  
  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
  
  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  .slide-in-left {
    animation: slideInLeft 0.3s ease-out forwards;
  }
  
  .slide-in-right {
    animation: slideInRight 0.3s ease-out forwards;
  }
  
  @keyframes slideInLeft {
    from {
      transform: translateX(-10px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes slideInRight {
    from {
      transform: translateX(10px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  .animate-pulse-slow {
    animation: pulseSlow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  
  @keyframes pulseSlow {
    0%, 100% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
  }
  
  .animation-delay-200 {
    animation-delay: 200ms;
  }
  
  .animation-delay-400 {
    animation-delay: 400ms;
        }
        
        .fade-zoom-in {
          animation: fadeZoomIn 0.3s ease-out forwards;
        }
        
  @keyframes fadeZoomIn {
    from {
      opacity: 0;
      transform: scale(0.98);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .animate-fade-in-up {
    animation: fadeInUp 0.3s ease-out forwards;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Video mirroring classes */
  .mirrored {
    transform: scaleX(-1) !important;
  }
  
  .non-mirrored {
    transform: scaleX(1) !important;
  }
  
  .animate-ping-slow {
    animation: pingSlow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
  
  @keyframes pingSlow {
    75%, 100% {
      transform: scale(1.5);
      opacity: 0;
    }
  }
`;

/**
 * InterviewPageContent Component
 * 
 * This component handles the main UI of the interview page,
 * consuming data and handlers from the InterviewContext
 */
const InterviewPageContent: React.FC = () => {
  const { isMobile } = useResponsive();
  const isMobileView = isMobile || window.innerWidth < 768;
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const interviewId = params.id || '';
  const userInterviewId = location.state?.userInterviewId;
  const interviewDetailsFromState = location.state?.interviewDetails;
  const { showToast } = useToast();
  
  // Extract implementor unique ID from location state or interview details
  const implementorUniqueIdFromState = location.state?.implementorUniqueId || interviewDetailsFromState?.implementorUniqueId;
  
  // Check if this is an implementor interview (for proctoring - camera ON but no video sent to Gemini)
  const isImplementorInterview = !!implementorUniqueIdFromState;
  
  // Add mobile message sheet state
  const [showMobileMessageSheet, setShowMobileMessageSheet] = useState(false);
  
  // Fixed capture interval for cost optimization
  const captureInterval = 2000; // Fixed 2 seconds interval
  
  // Check for completed interview on component mount
  useEffect(() => {
    const checkCompletedInterview = () => {
      // Check if the current interview is marked as completed
      const completedInterviewId = sessionStorage.getItem('completed_interview_id');
      
      if (completedInterviewId === interviewId) {
        console.log('[InterviewPage] This interview has already been completed, redirecting to dashboard');
        
        // Check if we're still processing the completion
        if (window.isProcessingInterviewCompletion) {
          console.log('[InterviewPage] Interview completion is still in progress, waiting...');
          return; // Don't navigate yet, wait for completion
        }
        
        // Set a flag to show toast after navigation
        sessionStorage.setItem('show_completed_toast', 'true');
        
        // Navigate to dashboard
        navigate('/', { replace: true });
      }
    };
    
    // Run the check immediately, then set interval to check periodically
    checkCompletedInterview();
    const intervalId = setInterval(checkCompletedInterview, 1000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [interviewId, navigate]);
  
  // Reference to prevent multiple initializations
  const hasInitializedRef = useRef(false);
  const pageIdRef = useRef(`interview-${Date.now()}`);
  const shouldAttemptConnectionRef = useRef(false);
  
  // Get all the state and handlers from the interview context
  const { 
    videoRef, 
    showMessageInput,
    isCameraEnabled,
    isMicEnabled,
    isScreenSharing,
    toggleCamera,
    toggleMicrophone,
    toggleScreenShare,
    toggleMessageInput,
    isDeviceDropdownOpen,
    toggleDeviceDropdown,
    handleEndInterview: contextHandleEndInterview,
    videoDevices,
    audioDevices,
    selectedVideoDevice,
    selectedAudioDevice,
    handleDeviceChange,
    showEndInterviewConfirmation,
    setShowEndInterviewConfirmation,
    showRestartConfirmation,
    setShowRestartConfirmation,
    connectionErrorVisible,
    connectionErrorType,
    setConnectionErrorVisible,
    setConnectionErrorType,
    confirmRestartInterview,
    confirmEndInterview,
    sendUserMessage,
    videoStream,
    updateVideoStream,
    SetInterviewDetails,
    SetUserInterviewId,
    connected,
    isMicMuted,
    showStopScreenShareConfirmation,
    setShowStopScreenShareConfirmation,
    stopScreenSharing,
    timerCompleted,
    setTimerCompleted,
    isGeneratingFeedback,
    isExitingInterview,
    subscriptionMinutesConsumed,
    elapsedTime,
  } = useInterview();
  
  // For implementor interviews, force camera to be ON when page loads (for proctoring)
  // IMPORTANT: This must run BEFORE media initialization to ensure camera is requested
  // Use a ref to track if we've already forced camera ON
  const hasForcedCameraOnRef = useRef(false);
  
  // Set camera state immediately for implementor interviews (before any effects run)
  if (isImplementorInterview && !hasForcedCameraOnRef.current && !isCameraEnabled) {
    hasForcedCameraOnRef.current = true;
    console.log('[InterviewPage] Implementor interview detected - forcing camera ON for proctoring (synchronous)');
    toggleCamera(); // This sets the state immediately
  }
  
  // Get the liveAPI context directly
  const liveAPI = useLiveAPIContext();
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(MultimodalLiveClient.isQuotaExceeded());
  const [isReconnecting, setIsReconnecting] = useState(false);
  const wasDisconnectedRef = useRef(false);
  const [showShortInterviewWarning, setShowShortInterviewWarning] = useState(false);

  // Track connection state more reliably
  const [connectionState, setConnectionState] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  
  // Use refs for values that shouldn't trigger re-renders
  const connectionLockRef = useRef(false);
  const connectionAttemptsRef = useRef(0);
  const mountedRef = useRef(true);
  const lastConnectionAttemptTimeRef = useRef(0);
  const connectionStabilityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Track if the component is stable (has been mounted for more than 5 seconds)
  const [isComponentStable, setIsComponentStable] = useState(false);
  
  // Enhanced stability tracking
  const [isComponentFullyStable, setIsComponentFullyStable] = useState(false);
  const stabilityTimersRef = useRef<NodeJS.Timeout[]>([]);

  // Use the screen share tracker to keep browser and app state in sync
  useScreenShareTracker(isScreenSharing, videoStream, stopScreenSharing);

  const handleConfirmEndInterview = useCallback(async () => {
    const finalElapsedTime = elapsedTime;

    if (isScreenSharing) {
      try {
        await stopScreenSharing();
      } catch (error) {
        console.warn('[InterviewPageContent] Failed to stop screen sharing before ending interview:', error);
      }
    }

    if (isCameraEnabled) {
      toggleCamera();
    }

    await confirmEndInterview(finalElapsedTime);
  }, [elapsedTime, isCameraEnabled, isScreenSharing, stopScreenSharing, toggleCamera, confirmEndInterview]);

  const handleEndInterview = useCallback(() => {
    if (elapsedTime < 300) {
      setShowShortInterviewWarning(true);
    } else {
      contextHandleEndInterview();
    }
  }, [elapsedTime, contextHandleEndInterview]);
  
  
  
  // Set the interview details in the interview context
  useEffect(() => {
    if (userInterviewId) {
      console.log('[InterviewPageContent] Setting user interview id:', userInterviewId);
      SetUserInterviewId(userInterviewId);
      interviewDetailsFromState.userInterviewId = userInterviewId;
    }

    if (interviewDetailsFromState) {
      console.log('[InterviewPageContent] Setting interview details:', interviewDetailsFromState);
      SetInterviewDetails(interviewDetailsFromState);
    } else {
      console.log('[InterviewPageContent] No user interview id found, skipping');
    }
    
  }, [interviewDetailsFromState, SetInterviewDetails, userInterviewId, SetUserInterviewId]);
  
  // Listen for quota exceeded events
  useEffect(() => {
    const handleQuotaExceeded = (event: CustomEvent) => {
      console.log('[InterviewPageContent] Quota exceeded event received:', event.detail);
      setIsQuotaExceeded(true);
      
      // Set connection error UI state
      setConnectionErrorType('quota_exceeded');
      setConnectionErrorVisible(true);
      
      // Immediately mark connection as not connected
      // This is important to prevent UI showing connected state when quota is exceeded
      if (liveAPI && typeof liveAPI.disconnect === 'function') {
        console.log('[InterviewPageContent] Performing disconnect due to quota exceeded');
        liveAPI.disconnect().catch(err => {
          console.error('[InterviewPageContent] Error disconnecting after quota exceeded:', err);
        });
      }
    };
    
    // Register the event listener
    window.addEventListener('geminiQuotaExceeded', handleQuotaExceeded as EventListener);
    
    return () => {
      window.removeEventListener('geminiQuotaExceeded', handleQuotaExceeded as EventListener);
    };
  }, [setConnectionErrorType, setConnectionErrorVisible, liveAPI]);
  
  // Effect to initialize and maintain audio/video when component mounts
  useEffect(() => {
    // Only run this effect once on component mount
    if (hasInitializedRef.current) {
      console.log(`[InterviewPage] [${pageIdRef.current}] Already initialized, skipping duplicate initialization`);
      return;
    }
    
    hasInitializedRef.current = true;
    console.log(`[InterviewPage] [${pageIdRef.current}] Initializing media streams...`);

    // Function to get media streams based on user selections
    const initializeMedia = async () => {
      try {
        // Start with any existing stream or get a new one
        if (!videoStream) {
          console.log('[InterviewPage] Getting new media stream...');
          
          // Build constraints based on device selections and camera state
          const constraints: MediaStreamConstraints = {
            // Only request video if camera is enabled
            video: isCameraEnabled ? (
              selectedVideoDevice 
                ? { deviceId: { exact: selectedVideoDevice } } 
                : true
            ) : false,
            audio: selectedAudioDevice 
              ? { deviceId: { exact: selectedAudioDevice }, 
                  echoCancellation: true,
                  noiseSuppression: true,
                  autoGainControl: true } 
              : { 
                  echoCancellation: true,
                  noiseSuppression: true,
                  autoGainControl: true 
                }
          };
          
          console.log('[InterviewPage] Using media constraints:', constraints);
          
          // Request media access
          try {
            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            console.log('[InterviewPage] Media stream obtained:', mediaStream);
            
            // Set tracks enabled based on user preferences (should already be respected by constraints)
            mediaStream.getVideoTracks().forEach(track => {
              console.log(`[InterviewPage] Setting video track ${track.id} enabled:`, isCameraEnabled);
              track.enabled = isCameraEnabled;
            });
            
            mediaStream.getAudioTracks().forEach(track => {
              console.log(`[InterviewPage] Setting audio track ${track.id} enabled:`, isMicEnabled);
              track.enabled = isMicEnabled;
            });
            
            // Update video stream in context
            updateVideoStream(mediaStream);
            
            // Attach stream to video element
            if (videoRef.current) {
              videoRef.current.srcObject = mediaStream;
              videoRef.current.play().catch(err => {
                console.error('[InterviewPage] Error playing video:', err);
              });
            } else {
              console.warn('[InterviewPage] Video element reference not available');
            }
          } catch (error) {
            console.error('[InterviewPage] Error accessing media devices:', error);
            
            // Try fallback with less strict constraints
            try {
              const fallbackConstraints: MediaStreamConstraints = {
                // Only request video if camera is enabled
                video: isCameraEnabled ? true : false,
                audio: isMicEnabled ? {
                  echoCancellation: true,
                  noiseSuppression: true,
                  autoGainControl: true
                } : false
              };
              
              console.log('[InterviewPage] Trying fallback constraints:', fallbackConstraints);
              const fallbackStream = await navigator.mediaDevices.getUserMedia(fallbackConstraints);
              
              // Update stream in context
              updateVideoStream(fallbackStream);
              
              // Attach to video element
              if (videoRef.current) {
                videoRef.current.srcObject = fallbackStream;
                videoRef.current.play().catch(err => {
                  console.error('[InterviewPage] Error playing fallback video:', err);
                });
              }
            } catch (fallbackError) {
              console.error('[InterviewPage] Fallback media request also failed:', fallbackError);
            }
          }
        } else {
          console.log('[InterviewPage] Using existing stream:', videoStream);
          
          // Ensure tracks are enabled/disabled according to user preferences
          const videoTracks = videoStream.getVideoTracks();
          videoTracks.forEach(track => {
            console.log(`[InterviewPage] Setting existing video track ${track.id} enabled:`, isCameraEnabled);
            track.enabled = isCameraEnabled;
          });
          
          const audioTracks = videoStream.getAudioTracks();
          audioTracks.forEach(track => {
            console.log(`[InterviewPage] Setting existing audio track ${track.id} enabled:`, isMicEnabled);
            track.enabled = isMicEnabled;
          });
          
          // Ensure stream is connected to video element
          if (videoRef.current) {
            if (videoRef.current.srcObject !== videoStream) {
              videoRef.current.srcObject = videoStream;
              videoRef.current.play().catch(err => {
                console.error('[InterviewPage] Error playing existing video:', err);
              });
            }
          } else {
            console.warn('[InterviewPage] Video element reference not available for existing stream');
          }
        }
      } catch (error) {
        console.error('[InterviewPage] Error initializing media:', error);
      }
    };
    
    // Initialize media
    initializeMedia();
    
    // Clean up function
    return () => {
      const cleanupId = Date.now().toString().slice(-6);
      console.log(`[InterviewPage][${cleanupId}] Cleaning up media streams on component unmount for page ${pageIdRef.current}...`);
      
      // Ensure any pending network operations are complete
      const performCleanup = async () => {
        try {
          // Release all media resources when unmounting
          if (videoStream) {
            // Get all tracks from the stream
            const allTracks = videoStream.getTracks();
            
            // Log what we're cleaning up
            console.log(`[InterviewPage][${cleanupId}] Cleaning up ${allTracks.length} media tracks on unmount`);
            
            // Stop each track to fully release device resources
            allTracks.forEach(track => {
              try {
                // First disable the track
                track.enabled = false;
                
                // Then stop it to release resources
                track.stop();
                
                console.log(`[InterviewPage][${cleanupId}] Released ${track.kind} track: ${track.id}`);
              } catch (err) {
                console.error(`[InterviewPage][${cleanupId}] Error stopping ${track.kind} track:`, err);
              }
            });
            
            // Clear video element source to help browser release resources
            if (videoRef.current) {
              try {
                videoRef.current.pause();
                videoRef.current.srcObject = null;
                // Force a reload of the video element
                videoRef.current.load();
                console.log(`[InterviewPage][${cleanupId}] Cleared video element source`);
              } catch (err) {
                console.error(`[InterviewPage][${cleanupId}] Error clearing video source:`, err);
              }
            }
            
            // Set the videoStream to null in the context (through the updateVideoStream function)
            updateVideoStream(null);
          }
          
          // Force device enumeration refresh to help browsers update device status
          try {
            await navigator.mediaDevices.enumerateDevices()
              .then(() => console.log(`[InterviewPage][${cleanupId}] Refreshed device enumeration`))
              .catch(err => console.warn(`[InterviewPage][${cleanupId}] Error refreshing device enumeration:`, err));
          } catch (err) {
            console.warn(`[InterviewPage][${cleanupId}] Error during device enumeration:`, err);
          }
            
          // Close any open WebSocket connections that might be in global scope
          if (window.mocktrailWsConnections && window.mocktrailWsConnections.length > 0) {
            console.log(`[InterviewPage][${cleanupId}] Closing ${window.mocktrailWsConnections.length} websocket connections...`);
            
            // Create a copy to avoid modification during iteration
            const connections = [...window.mocktrailWsConnections];
            
            for (let i = 0; i < connections.length; i++) {
              try {
                const ws = connections[i];
                if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                  ws.onclose = null; // Remove event handlers
                  ws.onerror = null;
                  ws.close(1000, "Page navigation");
                  console.log(`[InterviewPage][${cleanupId}] Closed websocket connection #${i}`);
                }
              } catch (err) {
                console.error(`[InterviewPage][${cleanupId}] Error closing websocket connection #${i}:`, err);
              }
            }
            
            window.mocktrailWsConnections = [];
          }
          
          // Clear any global timers
          if (window.altairTimers && window.altairTimers.length > 0) {
            console.log(`[InterviewPage][${cleanupId}] Clearing ${window.altairTimers.length} timers...`);
            window.altairTimers = [];
          }
          
          // Add an additional step to help Chrome release camera LED
          try {
            // Request a blank stream to help reset browser state
            const emptyStream = await navigator.mediaDevices.getUserMedia({video: false, audio: false});
            // Immediately stop it
            emptyStream.getTracks().forEach(t => t.stop());
            console.log(`[InterviewPage][${cleanupId}] Extra cleanup: Used empty stream to help reset browser state`);
          } catch (err) {
            // This is an extra step, so just log and continue
            console.log(`[InterviewPage][${cleanupId}] Empty stream request failed (non-critical):`, err);
          }
          
          console.log(`[InterviewPage][${cleanupId}] Cleanup completed successfully`);
        } catch (error) {
          console.error(`[InterviewPage][${cleanupId}] Error during cleanup:`, error);
        }
      };
      
      // Perform the cleanup
      performCleanup().finally(() => {
        // Reset initialization flag only if we're unmounting this specific instance
        console.log(`[InterviewPage][${cleanupId}] Cleanup completed for page ${pageIdRef.current}`);
      });
    };
  // Only depend on videoRef and videoStream, not isCameraEnabled or isMicEnabled
  // This prevents re-initialization when toggling camera/mic
  }, [videoRef, videoStream, updateVideoStream, selectedVideoDevice, selectedAudioDevice, isCameraEnabled, isMicEnabled]);
  
  // Set a flag to indicate the component is stable after 5 seconds
  useEffect(() => {
    // Only set up stability timers if we're not already stable
    if (isComponentStable && isComponentFullyStable) {
      return;
    }
    
    console.log(`[InterviewPageContent][${pageIdRef.current}] Setting up stability timers`);
    
    const stabilityTimer = setTimeout(() => {
      if (mountedRef.current) {
        console.log(`[InterviewPageContent][${pageIdRef.current}] Component is now considered stable after 5s`);
        setIsComponentStable(true);
        shouldAttemptConnectionRef.current = true;
      }
    }, 5000);
    
    // Setup additional stability timers to track component stability
    // Clear any existing timers to prevent memory leaks
    stabilityTimersRef.current.forEach(timer => clearTimeout(timer));
    stabilityTimersRef.current = [];
    
    // Define a series of increasingly longer stability checkpoints
    // Reduced to just 2 checkpoints to avoid too much delay
    const stabilityCheckpoints = [2000, 5000];
    
    // Create a timer for each checkpoint
    stabilityCheckpoints.forEach((delay, index) => {
      const timer = setTimeout(() => {
        // Only proceed if component is still mounted
        if (mountedRef.current) {
          console.log(`[InterviewPageContent] Component stable for ${delay/1000}s checkpoint reached`);
          
          // Set full stability at the final checkpoint
          if (index === stabilityCheckpoints.length - 1) {
            console.log('[InterviewPageContent] Component considered fully stable now');
            setIsComponentFullyStable(true);
            
            // Set global flag for other components like InterviewContext to know the page is stable
            if (typeof window !== 'undefined') {
              window.interviewPageFullyStable = true;
            }
            
            // Once fully stable, we can try to connect
            if (connectionState === 'idle' && !isQuotaExceeded) {
              console.log('[InterviewPageContent] Component now fully stable, initiating connection');
              initializeConnection();
            }
          }
        }
      }, delay);
      
      // Store timer for cleanup
      stabilityTimersRef.current.push(timer);
    });
    
    return () => {
      clearTimeout(stabilityTimer);
      
      // Clear all stability timers on unmount
      stabilityTimersRef.current.forEach(timer => clearTimeout(timer));
      stabilityTimersRef.current = [];
    };
  }, [connectionState, isQuotaExceeded, isComponentStable, isComponentFullyStable]);
  
  // Set mounted flag on mount/unmount
  useEffect(() => {
    mountedRef.current = true;
    console.log(`[InterviewPageContent][${pageIdRef.current}] Component mounted at ${new Date().toISOString()}`);
    
    return () => {
      // Mark component as unmounted
      mountedRef.current = false;
      console.log(`[InterviewPageContent][${pageIdRef.current}] Component unmounting at ${new Date().toISOString()}`);
      
      // Reset connection lock
      connectionLockRef.current = false;
    };
  }, []);

  // Define connection function outside of useEffect for better reuse
  const initializeConnection = async () => {
    // Check if this is a forced reconnection
    const forceReconnect = location.state?._forceReconnect;
    
    // Skip if connection is already in progress or established
    if (!forceReconnect && (connectionState !== 'idle' || connectionLockRef.current)) {
      console.log(`[InterviewPageContent][${pageIdRef.current}] Connection already ${connectionState}, skipping`);
      return;
    }
    
    // Skip if component is unmounting
    if (!mountedRef.current) {
      console.log(`[InterviewPageContent][${pageIdRef.current}] Component unmounting, skipping connection`);
      return;
    }
    
    // Check if we have a quota exceeded error
    if (isQuotaExceeded) {
      console.log(`[InterviewPageContent][${pageIdRef.current}] Not attempting connection - quota exceeded`);
      setConnectionState('error');
      return;
    }
    
    // Prevent rapid reconnection attempts - skip this check on forced reconnection
    const now = Date.now();
    if (!forceReconnect && now - lastConnectionAttemptTimeRef.current < 3000) {
      console.log(`[InterviewPageContent][${pageIdRef.current}] Connection attempt too soon after previous attempt, skipping`);
      return;
    }
    
    // Skip if connection stability timeout is active - unless forced reconnection
    if (!forceReconnect && connectionStabilityTimeoutRef.current) {
      console.log(`[InterviewPageContent][${pageIdRef.current}] Connection stability period active, skipping connection attempt`);
      return;
    }
    
    // Track this attempt
    lastConnectionAttemptTimeRef.current = now;
    
    // Acquire connection lock to prevent parallel attempts
    connectionLockRef.current = true;
    
    // Set state to connecting
    setConnectionState('connecting');
    
    try {
      // Check if client exists
      if (!liveAPI.client) {
        console.log(`[InterviewPageContent][${pageIdRef.current}] Not initializing connection - no client`);
        connectionLockRef.current = false;
        setConnectionState('idle');
        return;
      }
      
      // Check if we're already connected
      let isAlreadyConnected = false;
      try {
        isAlreadyConnected = liveAPI.client.isConnected && liveAPI.client.isConnected();
      } catch (err) {
        console.warn(`[InterviewPageContent][${pageIdRef.current}] Error checking connection state:`, err);
      }
      
      // Skip if already connected, unless this is a forced reconnection
      if (isAlreadyConnected && !forceReconnect) {
        console.log(`[InterviewPageContent][${pageIdRef.current}] Client reports already connected`);
        connectionLockRef.current = false;
        setConnectionState('connected');
        return;
      } else if (isAlreadyConnected && forceReconnect) {
        console.log(`[InterviewPageContent][${pageIdRef.current}] Client reports already connected but forcing reconnection due to restart`);
        // Continue with reconnection despite being "connected"
      }
      
      // Check for max attempts
      if (connectionAttemptsRef.current >= 3) {
        console.log(`[InterviewPageContent][${pageIdRef.current}] Max connection attempts reached, showing error`);
        setConnectionErrorType('connection_error');
        setConnectionErrorVisible(true);
        connectionLockRef.current = false;
        setConnectionState('error');
        return;
      }
      
      console.log(`[InterviewPageContent][${pageIdRef.current}] Initiating connection (attempt ${connectionAttemptsRef.current + 1}/3)...`);
      
      // Increment attempt counter
      connectionAttemptsRef.current++;
      
      // Start a connection stability timeout
      if (connectionStabilityTimeoutRef.current) {
        clearTimeout(connectionStabilityTimeoutRef.current);
      }
      
      connectionStabilityTimeoutRef.current = setTimeout(() => {
        console.log(`[InterviewPageContent][${pageIdRef.current}] Connection stability period ended`);
        connectionStabilityTimeoutRef.current = null;
      }, 5000);
      
      // Add a timeout to avoid hanging
      const connectionPromise = liveAPI.connect();
      const timeoutPromise = new Promise<void>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Connection timeout after 10 seconds'));
        }, 10000);
      });
      
      // Attempt the connection with timeout
      await Promise.race([connectionPromise, timeoutPromise]);
      
      // Connection succeeded if we got here
      console.log(`[InterviewPageContent][${pageIdRef.current}] LiveAPI connection established successfully`);
      
      // Reset attempts on success
      connectionAttemptsRef.current = 0;
      
      // Update state
      setConnectionState('connected');
      
    } catch (error) {
      console.error(`[InterviewPageContent][${pageIdRef.current}] Error connecting:`, error);
      
      // Check for quota exceeded in the error
      const errorStr = String(error).toLowerCase();
      if (errorStr.includes('quota') || errorStr.includes('exceeded')) {
        console.log(`[InterviewPageContent][${pageIdRef.current}] Quota exceeded error detected`);
        setConnectionErrorType('quota_exceeded');
        setConnectionState('error');
      } else {
        // Set general connection error
        setConnectionErrorType('connection_error');
        setConnectionState('error');
      }
      
      setConnectionErrorVisible(true);
      
      // If not at max attempts, retry after a delay
      if (connectionAttemptsRef.current < 3) {
        const retryDelay = Math.pow(2, connectionAttemptsRef.current) * 1000;
        console.log(`[InterviewPageContent][${pageIdRef.current}] Will retry in ${retryDelay/1000}s (attempt ${connectionAttemptsRef.current}/3)`);
        
        setTimeout(() => {
          if (mountedRef.current) {
            connectionLockRef.current = false;
            setConnectionState('idle');
            initializeConnection();
          }
        }, retryDelay);
      } else {
        connectionLockRef.current = false;
      }
    }
  };

  // Initialize connection when component is stable
  useEffect(() => {
    // Check for force reconnect flag (after restart)
    const forceReconnect = location.state?._forceReconnect;
    
    if (forceReconnect) {
      console.log(`[InterviewPageContent][${pageIdRef.current}] Force reconnect flag detected, initiating connection regardless of state`);
      // Reset connection state to ensure clean start
      setConnectionState('idle');
      connectionLockRef.current = false;
      connectionAttemptsRef.current = 0;
      
      // Add a small delay to ensure component is ready
      setTimeout(() => {
        if (mountedRef.current) {
          initializeConnection();
        }
      }, 500);
    } 
    // Normal initialization when component is stable
    else if (isComponentStable && connectionState === 'idle' && !connectionLockRef.current && !isQuotaExceeded) {
      console.log(`[InterviewPageContent][${pageIdRef.current}] Component stable, initiating connection`);
      initializeConnection();
    }
  }, [isComponentStable, connectionState, isQuotaExceeded, location.state]);
  
  const backgroundGradient = {
    backgroundImage: 'radial-gradient(circle at top right, rgba(33, 97, 148, 0.03), transparent), radial-gradient(circle at bottom left, rgba(2, 132, 199, 0.02), transparent)'
  };

  useEffect(() => {
    // Only run this effect once on component mount
    const instanceId = `${pageIdRef.current}-${Date.now()}`;
    console.log(`[InterviewPage][${instanceId}] Component mounted - Adding stabilizing protection against remounts`);
    document.title = `${interviewDetailsFromState?.title || interviewDetailsFromState?.jobRole} | MockTrail`;
    
    // Force a GC and reset quota flags on mount
    if (typeof window !== 'undefined') {
      // Attempt to access MultimodalLiveClient constructor via window
      const LiveClientConstructor = (window as any).MultimodalLiveClient;
      if (LiveClientConstructor && typeof LiveClientConstructor.resetQuotaExceeded === 'function') {
        console.log('[InterviewPage] Resetting quota exceeded flag on mount');
        LiveClientConstructor.resetQuotaExceeded();
      }
      
      // Track mount/unmount cycles to help debug component lifecycle issues
      if (!window.mocktrailMountCycles) {
        window.mocktrailMountCycles = [];
      }
      
      // Store mount time for tracking - avoid instanceId to keep TypeScript happy
      const mountTime = Date.now();
      
      // Store mount information in the global array
      window.mocktrailMountCycles.push({
        component: 'InterviewPage',
        mountTime: mountTime
      });
      
      // Also track in sessionStorage to help detect remount loops
      try {
        // Get existing mount records - include instanceId in sessionStorage only
        const mountRecords: Array<{time: number, id: string}> = JSON.parse(sessionStorage.getItem('interview-mount-records') || '[]');
        
        // Add this mount with instanceId (only in sessionStorage)
        mountRecords.push({
          time: mountTime,
          id: instanceId
        });
        
        // Keep only the last 20 records
        const recentRecords = mountRecords.slice(-20);
        
        // Store back to session storage
        sessionStorage.setItem('interview-mount-records', JSON.stringify(recentRecords));
        
        // Count recent mounts
        const recentMounts = recentRecords.filter(record => Date.now() - record.time < 10000);
        
        // Store globally to avoid the need for refs that might trigger rerenders
        window._currentInterviewMountCount = recentMounts.length;
        
        if (recentMounts.length > 2) {
          console.warn(`[InterviewPage] WARNING: ${recentMounts.length} mounts detected in the last 10 seconds!`, recentMounts);
          
          // CRITICAL FIX: Add emergency recovery mechanism to break remount loops
          if (recentMounts.length > 5) {
            console.error('[InterviewPage] CRITICAL: Detected likely remount loop! Taking emergency action to stabilize.');
            
            // Check if already in recovery mode
            const isAlreadyInRecoveryMode = sessionStorage.getItem('interview-remount-recovery-active') === 'true';
            
            if (!isAlreadyInRecoveryMode) {
              // Set a flag to indicate we're in recovery mode
              sessionStorage.setItem('interview-remount-recovery-active', 'true');
              sessionStorage.setItem('interview-remount-recovery-time', Date.now().toString());
              
              // Force garbage collection if possible (requires special browser flags)
              if (window.gc) {
                try {
                  console.log('[InterviewPage] Attempting to force garbage collection to stabilize');
                  window.gc();
                } catch (err) {
                  // Ignore errors during emergency recovery
                }
              }
              
              // Close and clean up any WebSocket connections
              if (window.mocktrailWsConnections && window.mocktrailWsConnections.length > 0) {
                const connections = [...window.mocktrailWsConnections];
                window.mocktrailWsConnections = []; // Clear immediately to prevent interference
                
                connections.forEach(ws => {
                  if (ws) {
                    try {
                      // Remove all event handlers
                      ws.onopen = null;
                      ws.onclose = null;
                      ws.onerror = null;
                      ws.onmessage = null;
                      
                      // Close the connection if it's not already closed
                      if (ws.readyState !== WebSocket.CLOSED) {
                        ws.close(1000, "Emergency recovery");
                      }
                    } catch (err) {
                      // Ignore errors during emergency recovery
                    }
                  }
                });
              }
              
              // Set a timeout to clear the recovery mode flag
              setTimeout(() => {
                sessionStorage.removeItem('interview-remount-recovery-active');
                console.log('[InterviewPage] Recovery mode ended');
              }, 5000);
            } else {
              // Already in recovery mode - check duration
              const recoveryStartTime = parseInt(sessionStorage.getItem('interview-remount-recovery-time') || '0', 10);
              const timeInRecovery = Date.now() - recoveryStartTime;
              
              if (timeInRecovery > 10000) {
                // In recovery mode for too long - take drastic action
                console.error('[InterviewPage] CRITICAL: Recovery failed after 10 seconds, reloading page');
                setTimeout(() => {
                  window.location.reload();
                }, 1000);
              }
            }
          }
        }
      } catch (storageErr) {
        console.warn('[InterviewPage] Error working with session storage:', storageErr);
      }
    }
    
    // Enable background blur and set body classes
    document.body.classList.add('overflow-hidden', 'bg-gradient-to-br', 'from-blue-50', 'to-indigo-50');
    document.body.classList.remove('dark:bg-surface-dark');
    
    return () => {
      const cleanupId = Date.now().toString().slice(-6);
      console.log(`[InterviewPage][${instanceId}][${cleanupId}] Component unmounting, cleaning up resources`);
      document.title = 'MockTrail';
      
      // Reset body classes
      document.body.classList.remove('overflow-hidden', 'bg-gradient-to-br', 'from-blue-50', 'to-indigo-50');
      
      // Reset quota exceeded flag when leaving interview page
      if (typeof window !== 'undefined') {
        // Log component unmount timing
        console.log(`[InterviewPage] Component unmounted after ${Date.now() - parseInt(instanceId.split('-')[1])}ms`);
        
        // Access via the window object if available
        const LiveClientConstructor = (window as any).MultimodalLiveClient;
        if (LiveClientConstructor && typeof LiveClientConstructor.resetQuotaExceeded === 'function') {
          console.log('[InterviewPage] Resetting quota exceeded flag on unmount');
          LiveClientConstructor.resetQuotaExceeded();
        }
        
        // Try to access it via the client instance if available
        if (liveAPI && liveAPI.client && 
            liveAPI.client.constructor && 
            typeof (liveAPI.client.constructor as any).resetQuotaExceeded === 'function') {
          console.log('[InterviewPage] Resetting quota exceeded flag via client instance on unmount');
          (liveAPI.client.constructor as any).resetQuotaExceeded();
        }
        
        // Close any other WebSocket connections that might be active
        if (window.mocktrailWsConnections && window.mocktrailWsConnections.length > 0) {
          console.log(`[InterviewPage] Cleaning up ${window.mocktrailWsConnections.length} WebSocket connections on unmount`);
          
          const connections = [...window.mocktrailWsConnections];
          window.mocktrailWsConnections = []; // Clear first to prevent interference
          
          connections.forEach((ws, index) => {
            if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
              try {
                // Remove event handlers first
                ws.onopen = null;
                ws.onclose = null;
                ws.onerror = null;
                ws.onmessage = null;
                
                // Then close the connection
                ws.close(1000, "Page unload");
                console.log(`[InterviewPage] Closed WebSocket #${index} on unmount`);
              } catch (err) {
                console.error(`[InterviewPage] Error closing WebSocket #${index}:`, err);
              }
            }
          });
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Removed liveAPI dependency - mount/unmount logic should run once

  // Add a useEffect to handle reconnection
  useEffect(() => {
    // Don't show the reconnection indicator if we're showing a modal or generating feedback
    if (showEndInterviewConfirmation || showRestartConfirmation || isGeneratingFeedback) {
      // If we're showing any of these modals, hide the reconnection indicator
      if (isReconnecting) {
        console.log('[InterviewPage] Hiding reconnection indicator due to modal being shown');
        setIsReconnecting(false);
      }
      return;
    }

    // Show reconnection indicator when we have a connection drop but no error modal yet
    if (liveAPI && liveAPI.connected === false && !isGeneratingFeedback && !isExitingInterview) {
      console.log('[InterviewPage] Connection dropped, showing reconnection indicator');
      setIsReconnecting(true);
      wasDisconnectedRef.current = true;
      
      // Start a timeout to show an error modal if reconnection takes too long
      const reconnectionTimeout = setTimeout(() => {
        if (!liveAPI.connected && !isGeneratingFeedback) {
          console.log('[InterviewPage] Reconnection taking too long, showing error modal');
          setConnectionErrorType('connection_error');
          setConnectionErrorVisible(true);
          setIsReconnecting(false);
        }
      }, 10000); // 10 seconds timeout
      
      return () => clearTimeout(reconnectionTimeout);
    } else if (liveAPI && liveAPI.connected === true) {
      // Connection is restored
      if (isReconnecting || wasDisconnectedRef.current) {
        console.log('[InterviewPage] Connection restored successfully');
        setIsReconnecting(false);
        
        if (wasDisconnectedRef.current) {
          wasDisconnectedRef.current = false;
        }
      }
    }
  }, [
    liveAPI, 
    liveAPI?.connected, 
    connectionErrorVisible, 
    setConnectionErrorType, 
    setConnectionErrorVisible, 
    isReconnecting,
    showEndInterviewConfirmation,
    showRestartConfirmation,
    isGeneratingFeedback
  ]);

  // Handler to navigate back to dashboard
  const handleBackToDashboard = () => {
    console.log('[InterviewPageContent] Navigating back to dashboard due to short interview');
    // Close any active connections before navigating
    if (liveAPI && liveAPI.disconnect) {
      liveAPI.disconnect().catch(err => {
        console.error('[InterviewPageContent] Error disconnecting from API:', err);
      });
    }
    
    // Navigate to the dashboard
    navigate('/dashboard');
  };

  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(false);
  const welcomeAnimationShownRef = useRef(false);

  // Add effect to handle welcome animation
  useEffect(() => {
    // Only show welcome animation if:
    // 1. We haven't shown it before
    // 2. We're not reconnecting
    // 3. We're not generating feedback
    // 4. We're not showing subscription minutes consumed
    // 5. We're connected
    if (!welcomeAnimationShownRef.current && 
        !isReconnecting && 
        !isGeneratingFeedback && 
        !subscriptionMinutesConsumed &&
        connected) {
      
      // Show the animation
      setShowWelcomeAnimation(true);
      welcomeAnimationShownRef.current = true;

      // Hide after 2 seconds
      const timer = setTimeout(() => {
        setShowWelcomeAnimation(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isReconnecting, isGeneratingFeedback, subscriptionMinutesConsumed, connected]);

  return (
    <div 
      id="interview-capture-container"
      data-interview-page="true"
      className="min-h-screen max-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark overflow-hidden" 
      style={backgroundGradient}
    >
      
      {/* Socket Handlers */}
      <AudioSocketHandler 
        isActive={isMicEnabled && connected} 
        isMuted={isMicMuted} 
      />
      
      {/* Camera video socket handler */}
      <VideoSocketHandler 
        isActive={isCameraEnabled && connected && !isScreenSharing} 
        videoRef={videoRef}
        captureInterval={captureInterval} 
        isScreenSharing={false}
        isImplementorInterview={isImplementorInterview}
      />
      
      {/* Screen sharing socket handler - only active when screen sharing */}
      <VideoSocketHandler 
        isActive={isScreenSharing && connected} 
        videoRef={videoRef}
        captureInterval={captureInterval}
        isScreenSharing={true}
        isImplementorInterview={isImplementorInterview}
      />
      
      {/* Screenshot handler for implementor interviews */}
      <ScreenshotHandler 
        isActive={isImplementorInterview && connected}
        implementorUniqueId={implementorUniqueIdFromState}
      />
      
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      {/* Interview Navbar */}
      <InterviewNavbar isMobileView={isMobileView} />
      
      {/* Restart Confirmation Modal */}
      <RestartConfirmationModal 
        isOpen={showRestartConfirmation} 
        onClose={() => setShowRestartConfirmation(false)} 
        onConfirm={confirmRestartInterview} 
      />
      
      {/* End Interview Confirmation Modal */}
      <EndInterviewConfirmationModal 
        isOpen={showEndInterviewConfirmation} 
        onClose={() => setShowEndInterviewConfirmation(false)} 
        onConfirm={() => { void handleConfirmEndInterview(); }}
      />

      <ShortInterviewWarningModal
        isOpen={showShortInterviewWarning}
        onClose={() => setShowShortInterviewWarning(false)}
        onConfirm={() => { setShowShortInterviewWarning(false); void handleConfirmEndInterview(); }}
      />
      
      {/* Connection Error Modal */}
      <ConnectionErrorModal 
        visible={connectionErrorVisible && !connected} 
        onDismiss={() => setConnectionErrorVisible(false)} 
        errorType={connectionErrorType} 
      />
      
      {/* Timer Completion Modal */}
      <TimerCompletionModal 
        isOpen={timerCompleted}
        onClose={() => setTimerCompleted(false)}
        onSeeFeedback={() => { void handleConfirmEndInterview(); }}
      />
      
      {/* Subscription Minutes Modal */}
      <SubscriptionMinutesModal 
        isOpen={subscriptionMinutesConsumed && !isExitingInterview}
        onClose={() => {/* Do nothing, handled by modal */}}
        onSeeFeedback={() => { void handleConfirmEndInterview(); }}
        onBackToDashboard={handleBackToDashboard}
        elapsedTimeInSeconds={elapsedTime}
      />
      
      {/* Main content area */}
      <style>{cssVariables}</style>
      <InterviewMainContent isMobileView={isMobileView} />

      {/* Floating Message Input - Shows above the control bar when active */}
      {showMessageInput && (
        <div className="fixed bottom-24 left-0 right-0 z-40 px-3 md:px-6 mx-auto max-w-xl animate-fade-in-up">
          <MessageInput sendUserMessage={sendUserMessage} />
        </div>
      )}

      {/* Mobile Message Bottom Sheet */}
      <MessageBottomSheet 
        isOpen={showMobileMessageSheet}
        onClose={() => setShowMobileMessageSheet(false)}
        isMobileView={isMobileView}
      />

      {/* Control Bar (fixed at bottom) */}
      <ControlBar
        isMicEnabled={isMicEnabled}
        isCameraEnabled={isCameraEnabled}
        showMessageInput={showMessageInput}
        showStopScreenShareConfirmation={showStopScreenShareConfirmation}
        setShowStopScreenShareConfirmation={(show) => setShowStopScreenShareConfirmation(show)}
        confirmStopScreenShare={() => {
          stopScreenSharing();
          setShowStopScreenShareConfirmation(false);
        }}
        isDeviceDropdownOpen={isDeviceDropdownOpen}
        videoDevices={videoDevices}
        audioDevices={audioDevices}
        selectedVideoDevice={selectedVideoDevice}
        selectedAudioDevice={selectedAudioDevice}
        isScreenSharing={isScreenSharing}
        toggleMicrophone={toggleMicrophone}
        toggleCamera={toggleCamera}
        toggleScreenShare={toggleScreenShare}
        toggleMessageInput={toggleMessageInput}
        toggleDeviceDropdown={toggleDeviceDropdown}
        handleEndInterview={handleEndInterview}
        handleDeviceChange={handleDeviceChange}
        showMobileMessageSheet={showMobileMessageSheet}
        setShowMobileMessageSheet={setShowMobileMessageSheet}
      />

      {/* Add the WelcomeAnimation */}
      <WelcomeAnimation visible={showWelcomeAnimation} />

      {/* Add the ReconnectionIndicator */}
      <ReconnectionIndicator visible={isReconnecting && !isGeneratingFeedback && !subscriptionMinutesConsumed} />
      
      {/* Add the GeneratingFeedbackModal */}
      <GeneratingFeedbackModal visible={isGeneratingFeedback} isImplementorInterview={isImplementorInterview} />
    </div>
  );
};

/**
 * Main InterviewPage Component
 * 
 * This component is responsible for setting up the InterviewProvider
 * and rendering the InterviewPageContent
 */
const InterviewPage: React.FC<InterviewPageProps> = ({
  videoRef,
  videoStream,
  onInterviewComplete,
  onVideoStreamChange,
  userData,
  implementorUniqueId,
  isPublicInterview = false
}) => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const interviewId = params.id || '';

  const interviewDetailsFromState = location.state?.interviewDetails
  
  // Extract implementor unique ID from location state or interview details
  const implementorUniqueIdFromState = location.state?.implementorUniqueId || interviewDetailsFromState?.implementorUniqueId || implementorUniqueId;
  
  // DEBUG: Log what InterviewPage receives from navigation
  console.log('[InterviewPage] RECEIVED interview details from navigation:', JSON.stringify(interviewDetailsFromState, null, 2));
  console.log('[InterviewPage] RECEIVED voicePack:', interviewDetailsFromState?.voicePack);
  console.log('[InterviewPage] RECEIVED characterGender:', interviewDetailsFromState?.characterGender);
  console.log('[InterviewPage] RECEIVED interviewPrompt:', interviewDetailsFromState?.interviewPrompt);
  console.log('[InterviewPage] RECEIVED implementorUniqueId:', implementorUniqueIdFromState); 

  const onRestartInterview = () => {
    // Clean up resources before navigating
    console.log('[InterviewPage] Preparing to restart interview by cleaning up resources first');
    
    // Handle the WebSocket connection directly by removing session storage items
    try {
      console.log('[InterviewPage] Clearing WebSocket connection data from session storage');
      sessionStorage.removeItem('websocket_connection_active');
      sessionStorage.removeItem('websocket_connection_time');
      sessionStorage.removeItem('websocket_connection_id');
    } catch (err) {
      console.warn('[InterviewPage] Error clearing session storage:', err);
    }
    
    // Close any WebSocket connections
    if (window.mocktrailWsConnections && window.mocktrailWsConnections.length > 0) {
      console.log(`[InterviewPage] Closing ${window.mocktrailWsConnections.length} websocket connections before restart`);
      
      // Create a copy of the array to avoid modification during iteration
      const connections = [...window.mocktrailWsConnections];
      
      connections.forEach((ws, index) => {
        try {
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            // Remove all event handlers first to prevent automatic reconnection
            ws.onopen = null;
            ws.onclose = null;
            ws.onerror = null;
            ws.onmessage = null;
            
            // Close the connection with a clear reason
            ws.close(1000, "Interview restarting");
            console.log(`[InterviewPage] Closed websocket connection #${index}`);
          }
        } catch (err) {
          console.error(`[InterviewPage] Error closing websocket connection #${index}:`, err);
        }
      });
      
      // Clear the array
      window.mocktrailWsConnections = [];
    }
    
    // Clear any global timers
    if (window.altairTimers && window.altairTimers.length > 0) {
      console.log(`[InterviewPage] Clearing ${window.altairTimers.length} timers before restart`);
      window.altairTimers = [];
    }
    
    // Reset any global state related to the interview
    if (typeof MultimodalLiveClient !== 'undefined' && 
        typeof MultimodalLiveClient.resetQuotaExceeded === 'function') {
      console.log('[InterviewPage] Resetting quota exceeded flag');
      MultimodalLiveClient.resetQuotaExceeded();
    }
    
    // Clean up any audio contexts
    if (window.mocktrailAudioContexts && window.mocktrailAudioContexts.length > 0) {
      console.log(`[InterviewPage] Closing ${window.mocktrailAudioContexts.length} audio contexts`);
      window.mocktrailAudioContexts.forEach((ctx, index) => {
        try {
          if (ctx.state !== 'closed') {
            ctx.close();
            console.log(`[InterviewPage] Closed audio context #${index}`);
          }
        } catch (err) {
          console.error(`[InterviewPage] Error closing audio context #${index}:`, err);
        }
      });
      window.mocktrailAudioContexts = [];
    }
    
    // Force fresh instance by regenerating the key
    try {
      // Generate a guaranteed unique key with a UUID component
      const timestamp = Date.now();
      const randomPart = Math.random().toString(36).substring(2, 10);
      const newKey = `interview-page-${timestamp}-${randomPart}`;
      
      sessionStorage.setItem('interview-page-stable-key', newKey);
      window._stableInterviewKey = newKey;
      console.log(`[InterviewPage] Created new stable key for restart: ${newKey}`);
      
      // Also clear any previous mount records to prevent remount protection
      try {
        sessionStorage.removeItem('interview-mount-records');
        console.log('[InterviewPage] Cleared mount records from session storage');
      } catch (err) {
        // Ignore
      }
    } catch (err) {
      console.error('[InterviewPage] Error updating stable key:', err);
    }
    
    // Add a small delay to ensure all cleanup is completed before navigation
    setTimeout(() => {
      console.log('[InterviewPage] Navigating to restart interview after cleanup');
      
      // Now navigate to restart the interview with a clean slate
      navigate(`/interview/${interviewId}`, { 
        state: { 
          id: interviewId,
          interviewDetails: interviewDetailsFromState,
          
          // Add a restart timestamp to force components to treat this as a new session
          _restartTimestamp: Date.now(),
          
          // Add a flag to force reconnection after restart
          _forceReconnect: true
        },
        replace: true // Replace current history entry to prevent back button issues
      });
    }, 100);
  }
  // Handle missing details navigation in an effect
  useEffect(() => {
    if (!interviewDetailsFromState || !interviewId) {
       console.error("[InterviewPage] Missing interview details or ID on mount.");
       navigate('/dashboard');
    }
  }, [interviewDetailsFromState, interviewId, navigate]);

  if (!interviewDetailsFromState || !interviewId) {
    return <div>Loading...</div>;
  }

  // Render providers unconditionally once details are available
  return (
    <ErrorBoundary fallback={
    <div className="flex flex-col items-center justify-center h-screen text-center text-text-primary-light dark:text-text-primary-dark">
        <p>An error occurred loading the interview.</p>
        <p>Please try again.</p>
        <Button className="mt-4" size="lg" onClick={() => onRestartInterview()}>Retry</Button>
        </div>
        }
      >
      <LiveAPIProvider
        url={process.env.REACT_APP_INTERVIEW_PROXY_WS_URL}
        apiKey={''} // No need for API key for the interview proxy
        implementorUniqueId={implementorUniqueIdFromState} // Pass implementor unique ID if this is a public interview
        interviewDetails={(() => {
          const finalInterviewDetails = {
            ...interviewDetailsFromState,
            id: interviewDetailsFromState?.id|| '', // Add missing id mapping
            userInterviewId: interviewDetailsFromState?.userInterviewId || '',
            userName: userData?.name || '',
            interviewGoals: userData?.interviewGoals || null,
            interviewTitle: interviewDetailsFromState?.title || null,
            language: interviewDetailsFromState?.language || null,
            jobDescription: interviewDetailsFromState?.description || null,
            durationMinutes: interviewDetailsFromState?.duration || null, // Add missing duration mapping
            jobRole: interviewDetailsFromState?.jobRole || null,
            company: interviewDetailsFromState?.company || null,
            resume: interviewDetailsFromState?.selectedResume || null,
            voicePack: interviewDetailsFromState?.voicePack || null,
            characterGender: interviewDetailsFromState?.characterGender || null,
            interviewPrompt: interviewDetailsFromState?.interviewPrompt || null,
            series: interviewDetailsFromState?.series || null
          };
          
          // DEBUG: Log what's being passed to LiveAPIProvider
          console.log('[InterviewPage] PASSING to LiveAPIProvider:', JSON.stringify(finalInterviewDetails, null, 2));
          console.log('[InterviewPage] PASSING voicePack:', finalInterviewDetails?.voicePack);
          console.log('[InterviewPage] PASSING characterGender:', finalInterviewDetails?.characterGender);
          console.log('[InterviewPage] PASSING interviewPrompt:', finalInterviewDetails?.interviewPrompt);
          
          return finalInterviewDetails;
        })()}
      >
        <InterviewProvider
          interviewId={interviewId}
          interviewDetails={interviewDetailsFromState}
          videoRef={videoRef}
          videoStream={videoStream}
          onInterviewComplete={onInterviewComplete}
          onRestartInterview={onRestartInterview}
          onVideoStreamChange={onVideoStreamChange}
          userData={userData}
          speechRecognitionEnabled={false}
        >
          <InterviewPageContent />
        </InterviewProvider>
      </LiveAPIProvider>
    </ErrorBoundary>
  );
};

// Modified StableInterviewPage to prevent remounting cycles
const StableInterviewPage: React.FC<InterviewPageProps> = React.memo((props) => {
  // Get interviewId from URL params
  const params = useParams();
  const interviewId = params.id || null;
  const [stableKey] = useState(() => {
    if (interviewId) {
      // Use interviewId as the stable key
      return `interview-page-${interviewId}`;
    }
    // Fallback to session storage if interviewId is not available
    if (window._stableInterviewKey) {
      return window._stableInterviewKey;
    }
    const existingKey = sessionStorage.getItem('interview-page-stable-key');
    if (existingKey) {
      window._stableInterviewKey = existingKey;
      return existingKey;
    }
    const newKey = `interview-page-${Date.now()}`;
    try {
      sessionStorage.setItem('interview-page-stable-key', newKey);
      window._stableInterviewKey = newKey;
    } catch (err) {}
    return newKey;
  });
  
  // Maintain reference to unmounted state to prevent cleanup actions during React StrictMode
  const hasUnmountedRef = useRef(false);
  
  // Track if this is the first render
  const isFirstRender = useRef(true);
  
  // Use useMemo to create a stable props object, but update it when relevant props change.
  const memoizedProps = useMemo(() => {
    console.log('[StableInterviewPage] Recalculating memoized props');
    // We no longer need the isFirstRender check here, useMemo handles updates based on dependencies.
    return {
      videoRef: props.videoRef,
      videoStream: props.videoStream,
      onInterviewComplete: props.onInterviewComplete,
      onVideoStreamChange: props.onVideoStreamChange,
      userData: props.userData || { name: 'User', profileImage: '' } // Provide default userData
    };
  }, [ // Add props that should trigger an update to the dependencies
     props.videoRef, 
     props.videoStream, 
     props.onInterviewComplete, 
     props.onVideoStreamChange, 
     props.userData
  ]); 
  
  // Log information about this component instance
  useEffect(() => {
    console.log(`[StableInterviewPage] Created stable instance with key: ${stableKey}`);
    
    // Reset any global state on mount
    if (typeof MultimodalLiveClient !== 'undefined' && 
        typeof MultimodalLiveClient.resetQuotaExceeded === 'function') {
      console.log('[StableInterviewPage] Resetting quota exceeded flag on mount');
      MultimodalLiveClient.resetQuotaExceeded();
    }
    
    // Clean up the session storage key when done with the interview
    const cleanupSessionStorage = () => {
      // Only clean up when navigating away from interview page
      if (!window.location.pathname.includes('/interview/')) {
        try {
          sessionStorage.removeItem('interview-page-stable-key');
          delete window._stableInterviewKey;
          console.log('[StableInterviewPage] Cleaned up session storage key');
        } catch (err) {
          console.warn('[StableInterviewPage] Error cleaning up session storage:', err);
        }
      }
    };
    
    // Listen for navigation events to clean up
    window.addEventListener('popstate', cleanupSessionStorage);
    
    // Reset unmounted flag on each mount
    hasUnmountedRef.current = false;
    
    // Add stabilization cleanup
    return () => {
      console.log(`[StableInterviewPage] Unmounting stable instance with key: ${stableKey}`);
      
      // Mark as unmounted to prevent any new state updates
      hasUnmountedRef.current = true;
      
      // Remove event listener
      window.removeEventListener('popstate', cleanupSessionStorage);
      
      // Force reset any global state related to the interview
      if (typeof MultimodalLiveClient !== 'undefined' && 
          typeof MultimodalLiveClient.resetQuotaExceeded === 'function') {
        MultimodalLiveClient.resetQuotaExceeded();
      }
      
      // Cleanup any orphaned WebSocket connections in the global registry
      if (typeof window !== 'undefined' && window.mocktrailWsConnections) {
        const connectionsCount = window.mocktrailWsConnections.length;
        
        if (connectionsCount > 0) {
          console.log(`[StableInterviewPage] Cleaning up ${connectionsCount} orphaned WebSocket connections`);
          
          // Close all the websockets
          window.mocktrailWsConnections.forEach((ws, index) => {
            try {
              if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) {
                ws.close(1000, "Component unmounted");
                console.log(`[StableInterviewPage] Closed orphaned WebSocket ${index+1}/${connectionsCount}`);
              }
            } catch (err) {
              console.warn(`[StableInterviewPage] Error closing orphaned WebSocket:`, err);
            }
          });
          
          // Clear the array
          window.mocktrailWsConnections = [];
        }
      }
    };
  }, [stableKey]);
  
  // Return the InterviewPage with a stable key and memoized props
  return <InterviewPage 
    key={stableKey} 
    {...memoizedProps} 
  />;
});

// Set display name for debugging
StableInterviewPage.displayName = 'StableInterviewPage';

// Export the wrapped component
// export default withPageTransition(StableInterviewPage);  // Temporarily remove HOC
export default StableInterviewPage; // Export directly 
